import { useCallback, useState } from "react";
import { container } from "tsyringe";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Input from "components/materials/Form/InputElement";
import { TextArea } from "components/materials/Form/TextAreaElement";
import I18n from "components/materials/I18n";
import UpcomingActionRequestResource from "common/resources/Project/Config/UpcomingAction/UpcomingActionRequestResource";
import UpcomingActionResponseResource from "common/resources/Project/Config/UpcomingAction/UpcomingActionResponseResource";
import { useProjectId } from "hooks/useProjectId";
import UpcomingActionService from "services/projects/UpcomingActionService";
import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (action: UpcomingActionResponseResource) => void;
};

const upcomingActionService = container.resolve(UpcomingActionService);

export default function CreateActionModal(props: IProps) {
	const [isLoading, setIsLoading] = useState(false);
	const { formRef, errors, handleError, clearErrors } = useForm();
	const { projectId } = useProjectId();

	const onCreateAction = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();
			setIsLoading(true);

			const upcomingActionRequestResource = UpcomingActionRequestResource.hydrate<UpcomingActionRequestResource>({
				title: [{ lang: I18n.getLang(), text: formData["title"] as string }],
				description: [{ lang: I18n.getLang(), text: formData["description"] as string }],
				buttonLabel: formData["button-label"] ? [{ lang: I18n.getLang(), text: formData["button-label"] as string }] : undefined,
				buttonLink: formData["button-link"] ? (formData["button-link"] as string) : undefined,
				startsAt: new Date(formData["start-date"] as string),
				endsAt: new Date(formData["end-date"] as string),
			});

			const image = (formData["image"] as FileList)?.[0];

			upcomingActionRequestResource
				.validateOrReject()
				.then(() => {
					upcomingActionService
						.create(projectId, upcomingActionRequestResource, image)
						.then((resource) => {
							props.onCreated(resource);
							setIsLoading(false);
						})
						.catch((error) => {
							handleError(error);
							setIsLoading(false);
						});
				})
				.catch((error) => {
					handleError(error);
					setIsLoading(false);
				});
		},
		[clearErrors, handleError, projectId, props],
	);

	return (
		<Modal isOpen onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.actions.create_action_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onCreateAction} errors={errors} formRef={formRef} className={classes["form-container"]}>
				<div className={classes["inputs-container"]}>
					<Input
						maxLength={50}
						name="title"
						required
						label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_title}
						additionalInfo={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_title_description}
						className={classes["input"]}
					/>
					<TextArea
						name="description"
						required
						label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_description}
						className={classes["input"]}
					/>
					<DragAndDropFile
						name="image"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_image}
						description={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_image_description}
						formRef={formRef}
						className={classes["drag-and-drop"]}
					/>
					<Input name="button-label" label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_button_label} className={classes["input"]} />
					<Input name="button-link" label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_button_link} className={classes["input"]} />
					<Input
						name="start-date"
						required
						type="datetime-local"
						label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_start_date}
						className={classes["input"]}
					/>
					<Input
						name="end-date"
						required
						type="datetime-local"
						label={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_end_date}
						className={classes["input"]}
					/>
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose} disabled={isLoading}>
						{I18n.asset.pages.professional_space.tabs.actions.create_action_modal.cancel}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} isLoading={isLoading}>
						{I18n.asset.pages.professional_space.tabs.actions.create_action_modal.create_action}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
